import React from "react";
import PropTypes from "prop-types";
import DocumentService from "services/documents-service";
import { Icon, ICONS } from "./ui";

/**
 * A document's own picture, small.
 *
 * Files are served behind auth, so a bare `<img src>` at the download URL
 * renders a broken image — which is why the categorisation list showed none.
 * This pulls the file the same way the Credit Checker viewer does, through
 * DocumentService, and hands the blob to an `<img>`.
 *
 * Blob URLs are cached per path for the life of the tab: the same file is
 * drawn in the list, the detail pane and the viewer, and re-fetching it each
 * time is both slow and pointless. Only pictures are fetched — anything else
 * keeps its glyph.
 */

const cache = new Map();
const MAX_CACHED = 60;

export const looksLikeImage = doc => {
    if (!doc) return false;
    const mime = String(doc.mime || "").toLowerCase();
    if (mime.indexOf("image/") === 0) return true;
    if (mime) return false;
    return /\.(png|jpe?g|gif|webp|bmp|tiff?)(\?|$)/i.test(String(doc.fileUrl || ""));
};

const loadBlobUrl = path => {
    if (cache.has(path)) return Promise.resolve(cache.get(path));
    return DocumentService.getDocumentUrl(path).then(({ data, headers }) => {
        const type = (headers && headers["content-type"]) || "";
        const url = URL.createObjectURL(new Blob([data], { type }));
        if (cache.size >= MAX_CACHED) {
            const oldest = cache.keys().next().value;
            URL.revokeObjectURL(cache.get(oldest));
            cache.delete(oldest);
        }
        cache.set(path, url);
        return url;
    });
};

class DocThumb extends React.Component {
    state = { url: null, failed: false };

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.doc !== this.props.doc) {
            this.setState({ url: null, failed: false }, this.load);
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    load() {
        const { doc } = this.props;
        if (!doc || !doc.fileUrl || !looksLikeImage(doc)) return;
        loadBlobUrl(doc.fileUrl)
            .then(url => {
                if (!this.unmounted) this.setState({ url });
            })
            .catch(() => {
                if (!this.unmounted) this.setState({ failed: true });
            });
    }

    render() {
        const { doc, size, onClick } = this.props;
        const { url } = this.state;
        const body = url ? (
            <img className={"ops-ws__thumb-img"} src={url} alt={doc.name} />
        ) : (
            <Icon d={ICONS[doc.thumbKind] || ICONS.file} size={Math.round(size * 0.42)} />
        );
        const className = "ops-ws__thumb" + (url ? " has-image" : "");
        const style = { width: size, height: size };
        if (!onClick) {
            return (
                <span className={className} style={style}>
                    {body}
                </span>
            );
        }
        return (
            <button
                type={"button"}
                className={className}
                style={style}
                title={"Open document"}
                onClick={onClick}
            >
                {body}
            </button>
        );
    }
}

DocThumb.defaultProps = { size: 40 };

DocThumb.propTypes = {
    doc: PropTypes.object.isRequired,
    size: PropTypes.number,
    onClick: PropTypes.func
};

export default DocThumb;
